import { useMemo, useState } from 'react';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { DataTable } from '@/components/tables/DataTable';
import { EditSheet } from '@/components/layout/EditSheet';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  useProjectCategories,
  useUpsertProjectCategory,
  useDeleteProjectCategory,
  type ProjectCategory,
} from '@/lib/admin-queries';
import { toast } from '@/components/ui/Toast';
import { slugify } from '@/lib/utils';
import { Input, Field } from '@/components/ui/Field';

const EMPTY = { name: '', slug: '', display_order: 0 };

export function ProjectCategoriesPage() {
  const { data: rows = [], isLoading } = useProjectCategories();
  const upsert = useUpsertProjectCategory();
  const remove = useDeleteProjectCategory();
  const [editing, setEditing] = useState<ProjectCategory | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<ProjectCategory | null>(null);

  const columns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Name',
        cell: (r: ProjectCategory) => <p className="font-medium">{r.name}</p>,
      },
      {
        key: 'slug',
        header: 'Slug',
        cell: (r: ProjectCategory) => (
          <span className="font-mono text-xs text-fg-muted">/{r.slug}</span>
        ),
      },
      {
        key: 'display_order',
        header: 'Order',
        cell: (r: ProjectCategory) => <span className="text-xs text-fg-muted">{r.display_order}</span>,
      },
      {
        key: 'actions',
        header: '',
        align: 'right' as const,
        cell: (r: ProjectCategory) => (
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              aria-label="Edit"
              onClick={(e) => {
                e.stopPropagation();
                setEditing(r);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-bg-subtle hover:text-fg"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              aria-label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                setDeleting(r);
              }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-fg-muted hover:bg-danger/15 hover:text-danger"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
      },
    ],
    [],
  );

  async function onSave(row: Partial<ProjectCategory> & { name: string; slug: string; display_order: number }) {
    try {
      await upsert.mutateAsync(row as ProjectCategory);
      toast.success(row.id ? 'Category updated' : 'Category created');
      setEditing(null);
      setCreating(false);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function onConfirmDelete() {
    if (!deleting?.id) return;
    try {
      await remove.mutateAsync(deleting.id);
      toast.success('Category deleted');
      setDeleting(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  function handleNew() {
    const nextOrder = rows.length > 0 ? Math.max(...rows.map((r) => r.display_order)) + 1 : 1;
    setEditing({ ...EMPTY, display_order: nextOrder, id: undefined });
    setCreating(true);
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="flex items-end justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Projects</p>
          <h1 className="mt-2 display-serif text-display-l text-fg">Categories</h1>
          <p className="mt-2 text-sm text-fg-muted">
            Manage project categories. {rows.length} categories.
          </p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={handleNew}>
          New category
        </Button>
      </header>

      {isLoading ? (
        <Card>
          <div className="space-y-3 p-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </Card>
      ) : (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(r) => r.id ?? r.slug}
          onRowClick={(r) => setEditing(r)}
          empty="No categories yet. Click New category to add one."
        />
      )}

      <EditSheet
        open={editing !== null}
        onOpenChange={(o) => {
          if (!o) {
            setEditing(null);
            setCreating(false);
          }
        }}
        title={creating ? 'New category' : 'Edit category'}
        description="The slug is auto-generated from the name."
        size="sm"
      >
        {editing && (
          <CategoryForm
            value={editing}
            submitting={upsert.isPending}
            onSubmit={onSave}
            onCancel={() => {
              setEditing(null);
              setCreating(false);
            }}
          />
        )}
      </EditSheet>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete this category?"
        description={deleting ? `"${deleting.name}" will be removed.` : ''}
        confirmLabel="Delete category"
        destructive
        loading={remove.isPending}
        onConfirm={onConfirmDelete}
      />
    </div>
  );
}

interface CategoryFormProps {
  value: ProjectCategory;
  submitting: boolean;
  onSubmit: (v: ProjectCategory) => void | Promise<void>;
  onCancel: () => void;
}

function CategoryForm({ value, submitting, onSubmit, onCancel }: CategoryFormProps) {
  const [v, setV] = useState({ ...value });
  const [errors, setErrors] = useState<Record<string, string>>({});

  function patch<K extends keyof ProjectCategory>(key: K, val: ProjectCategory[K]) {
    setV((cur) => ({ ...cur, [key]: val }));
  }

  function onNameBlur() {
    if (!v.slug || v.slug === slugify(value.name)) {
      patch('slug', slugify(v.name));
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!v.name.trim()) {
      setErrors({ name: 'Name is required' });
      return;
    }
    onSubmit(v as ProjectCategory);
  }

  return (
    <form id="category-form" onSubmit={handleSubmit} className="space-y-4">
      <Field id="name" label="Name" required error={errors.name}>
        {({ id, invalid }) => (
          <Input
            id={id}
            required
            invalid={invalid}
            value={v.name}
            onChange={(e) => patch('name', e.target.value)}
            onBlur={onNameBlur}
          />
        )}
      </Field>

      <Field id="slug" label="Slug" required error={errors.slug}>
        {({ id, invalid }) => (
          <Input
            id={id}
            required
            invalid={invalid}
            value={v.slug}
            onChange={(e) => patch('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
          />
        )}
      </Field>

      <Field id="display_order" label="Order">
        {({ id }) => (
          <Input
            id={id}
            type="number"
            value={v.display_order}
            onChange={(e) => patch('display_order', Number(e.target.value))}
          />
        )}
      </Field>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" form="category-form" loading={submitting}>
          {v.id ? 'Save changes' : 'Create category'}
        </Button>
      </div>
    </form>
  );
}
